import platformClient from "../../../node_modules/purecloud-platform-client-v2/src/purecloud-platform-client-v2/index.js";
import isEmpty from "lodash/fp/isEmpty";

class PureCloud {
  constructor(Events, $q, $httpParamSerializerJQLike) {
    "ngInject";
    this.Events = Events;
    this.q = $q;
    this.paramSerializer = $httpParamSerializerJQLike;
    this.usersApi = new platformClient.UsersApi();
    this.conversationsApi = new platformClient.ConversationsApi();
    this.notificationsApi = new platformClient.NotificationsApi();
    this.conversationList = {};
    this.CONVERSATION_LIST_TEMPLATE = null;
    this.conversationsTopic = "";
    this.clientId = "";
    this.redirectUri = "";
    this.redirectParams = {};
    this.uriHash = {};
    this.callState = "";

    this.successNotificationEvent = this.Events.eventsType.onShowSuccessNotification;
    this.errorNotificationEvent = this.Events.eventsType.onShowErrorNotification;
  }

  $onInit() {}

  /* This function copies properties from the participant's call object in a notification to the
   * participant object to make the participant object look the same as the response from the
   * conversations APIs. This isn't strictly necessary, but is helpful to maintain a consistent structure.
   */
  copyCallPropsToParticipant(conversation) {
    this.participants = conversation.participants;
    conversation.participants.forEach((participant) => {
      if (!participant.calls || participant.calls.length === 0) return;

      participant.ani = participant.calls[0].self.addressNormalized;
      participant.attributes = participant.additionalProperties;
      participant.confined = participant.calls[0].confined;
      participant.direction = participant.calls[0].direction;
      participant.dnis = participant.calls[0].other.addressNormalized;
      participant.held = participant.calls[0].held;
      participant.muted = participant.calls[0].muted;
      participant.provider = participant.calls[0].provider;
      participant.recording = participant.calls[0].recording;
      participant.recordingState = participant.calls[0].recordingState;
      participant.state = participant.calls[0].state;
      this.callState = participant.state;
      if (participant.userId) {
        participant.user = {
          id: participant.userId,
          selfUri: `/api/v2/users/${participant.userId}`,
        };
      }
      if (participant.calls[0].peerId) {
        participant.peer = participant.calls[0].peerId;
      }
    });
  }

  // Handle incoming PureCloud notification from WebSocket
  handleNotification(message) {
    // Parse notification string to a JSON object
    const notification = JSON.parse(message.data);

    // Discard unwanted notifications
    if (notification.topicName.toLowerCase() === "channel.metadata") {
      // Heartbeat
      return;
    }
    // Hearbeat
    // console.debug('Conversation notification: ', notification);
    // See function description for explanation
    this.copyCallPropsToParticipant(notification.eventBody);

    // Update conversation in list or remove it if disconnected
    if (this.isConversationDisconnected(notification.eventBody)) {
      delete this.conversationList[notification.eventBody.id];
    } else {
      this.conversationList[notification.eventBody.id] = notification.eventBody;
    }
  }

  // Determines if a conversation is disconnected by checking to see if all participants are disconnected
  isConversationDisconnected(conversation) {
    let isConnected = false;
    conversation.participants.some((participant) => {
      if (participant.state !== "disconnected") {
        isConnected = true;
        return true;
      }
      return false;
    });

    return !isConnected;
  }

  // Mute participant
  mute(callId, participantId, currentMuteState) {
    // Create request body, only set desired properties
    const body = {
      muted: !currentMuteState,
    };

    // Invoke API
    this.conversationsApi
      .patchConversationsCallParticipant(callId, participantId, body)
      .then(() => {
        if (body.muted) {
          this.Events.emit(this.successNotificationEvent, {
            message: "Call muted",
          });
        } else {
          this.Events.emit(this.successNotificationEvent, {
            message: "Call unmuted",
          });
        }
      })
      .catch(() => {
        this.Events.emit(this.errorNotificationEvent, {
          message: "Call can't be muted",
        });
      });
  }

  // Hold participant
  hold(callId, participantId, currentHoldState) {
    // Create request body, only set desired properties
    const body = {
      held: currentHoldState,
    };

    // Invoke API
    this.conversationsApi
      .patchConversationsCallParticipant(callId, participantId, body)
      .then(() => {
        if (body.held) {
          this.Events.emit(this.successNotificationEvent, {
            message: "Call on hold",
          });
        } else {
          this.Events.emit(this.successNotificationEvent, {
            message: "Call resumed",
          });
        }
      })
      .catch(() => {
        this.Events.emit(this.errorNotificationEvent, {
          message: "Call can't be held",
        });
      });
  }

  getParticipants() {
    return this.participants;
  }

  getCallId() {
    return this.pccallId;
  }

  // Disconnect participant
  disconnect(callId, participantId) {
    // Create request body, only set desired properties
    const body = {
      state: "disconnected",
    };

    // Invoke API
    this.conversationsApi
      .patchConversationsCallParticipant(callId, participantId, body)
      .then(() => {
        this.Events.emit(this.successNotificationEvent, {
          message: "Call disconnected successfully",
        });
      })
      .catch(() => {
        this.Events.emit(this.errorNotificationEvent, {
          message: "Call can't be disconnected",
        });
      });
  }

  getParameterByName(name) {
    name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\#&]" + name + "=([^&#]*)");

    const results = regex.exec(location.hash);
    return results === null
      ? ""
      : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  authenticate(redirectUri, clientId, redirectParams) {
    this.redirectUri = redirectUri;
    this.clientId = clientId;
    this.redirectParams = redirectParams;
    if (window.location.hash) {
      location.hash
        .slice(1)
        .split("&")
        .forEach((inst) => {
          const temp = inst.split("=");
          this.uriHash[temp[0]] = temp[1];
        });
    } else {
      const queryStringData = {
        response_type: "token",
        client_id: clientId,
        redirect_uri: redirectUri,
        // State to add extra paramters
        state: this.redirectParams,
      };
      this.uriHash =
        "https://login.mypurecloud.ie/oauth/authorize?" +
        this.paramSerializer(queryStringData);
      window.location.replace(this.uriHash);
    }
    return this.uriHash;
  }

  implicitLogin(clientId, redirectUri, redirectParams) {
    const client = platformClient.ApiClient.instance;
    const env = "mypurecloud.ie";
    client.setEnvironment(env);
    client.setPersistSettings(true, "optional_prefix");
    return client.loginImplicitGrant(clientId, redirectUri, {
      state: redirectParams,
    });
  }

  getSelfData() {
    return this.usersApi.getUsersMe();
  }

  // Subscribe to authenticated user's conversations
  createNotificationChannelSubscription(userData, channel) {
    // Set local vars
    let webSocket = {};
    const notificationChannel = channel;
    // Set up web socket
    webSocket = new WebSocket(notificationChannel.connectUri);
    webSocket.onmessage = this.handleNotification.bind(this);

    // Subscribe to authenticated user's conversations
    this.conversationsTopic = `v2.users.${userData.id}.conversations`;
    const body = [{ id: this.conversationsTopic }];
    return this.notificationsApi.putNotificationsChannelSubscriptions(
      notificationChannel.id,
      body
    );
  }

  createNotificationChannels() {
    // Create notification channel
    return this.notificationsApi.postNotificationsChannels();
  }

  postCall(phoneNumber) {
    const conversBody = {
      phoneNumber,
    };

    let userData = {};

    if (
      this.clientId !== "" &&
      this.redirectUri !== "" &&
      !isEmpty(this.redirectParams)
    ) {
      return this.q((resolve, reject) => {
        this.implicitLogin(this.clientId, this.redirectUri, this.redirectParams)
          .then(() => this.getSelfData())
          .then((selfData) => {
            userData = selfData;
            return this.createNotificationChannels();
          })
          .then((channel) =>
            this.createNotificationChannelSubscription(userData, channel)
          )
          .then(() => {
            // Invoke API
            return this.conversationsApi.postConversationsCalls(conversBody);
          })
          .then((data) => {
            this.pccallId = data.id;

            this.Events.emit(this.successNotificationEvent, {
              message: "Call placed successfully ...",
            });
            resolve(data);
          })
          .catch((err) => {
            reject(err);
            this.Events.emit(this.errorNotificationEvent, {
              message: "Error placing call ...",
            });
          });
      });
    }
  }
}

export default PureCloud;



// WEBPACK FOOTER //
// ./src/services/purecloud/purecloud.service.js